import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { storage, db } from "../../firebase";
import { ref as storageRef, listAll, getDownloadURL } from "firebase/storage";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "./PremiumTestPage.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function PremiumTestPage() {
  const navigate = useNavigate();
  const { courseId, testId } = useParams();
  const { currentUser } = useAuth();

  // State Management
  const [timeLeft, setTimeLeft] = useState(3 * 60 * 60); // 3 hours default
  const [currentQues, setCurrentQues] = useState(0);
  const [answers, setAnswers] = useState({});
  const [marked, setMarked] = useState([]);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [testMetadata, setTestMetadata] = useState(null);
  const [error, setError] = useState(null);

  const userName = currentUser?.displayName || currentUser?.email;

  // Load questions from Firebase Storage
  useEffect(() => {
    let cancelled = false;

    const fetchQuestions = async () => {
      setLoadingQuestions(true);
      try {
        if (!courseId || !testId) {
          setError("Invalid course or test ID");
          return;
        }

        // Determine category (JEE or NEET) from courseId
        const category = courseId.includes("jee") ? "JEE" : "NEET";
        const testPath = `PremiumTestSeries/${category}/${testId}`;

        const folderRef = storageRef(storage, testPath);
        const result = await listAll(folderRef);

        // Extract question files
        const questionFiles = result.items.filter(item => 
          item.name.match(/^question_\d+\.(jpg|png|jpeg)$/i)
        );

        // Load answer key from local repo using naming convention
        // Format: {category.toLowerCase()}_answerkey_{testId}_{testId}.json
        let answerKeyData = {};
        try {
          const answerKeyFileName = `${category.toLowerCase()}_answerkey_${testId.toLowerCase()}_${testId.toLowerCase()}`;
          // Try to import the answer key from answerKeys folder
          const answerKeyModule = await import(`../../answerKeys/${answerKeyFileName}.json`);
          answerKeyData = answerKeyModule.default || answerKeyModule;
        } catch (err) {
          console.warn(`Could not load answer key for ${testId}:`, err);
          // Answer key is optional, tests can work without it
        }

        // Load questions with metadata
        const items = await Promise.all(
          questionFiles.map(async (itemRef) => {
            const name = itemRef.name;
            const match = name.match(/question_(\d+)/);
            const id = match ? parseInt(match[1], 10) : null;
            
            let url = null;
            try {
              url = await getDownloadURL(storageRef(storage, `${testPath}/${name}`));
            } catch (err) {
              console.warn("Failed to get download URL for", name, err);
            }

            const ak = answerKeyData[id] !== undefined ? String(answerKeyData[id]).trim() : "";
            const isMcq = ak && ["1", "2", "3", "4"].includes(ak);

            return {
              id: id,
              question: "",
              img: url,
              type: isMcq ? "mcq" : "numerical",
              correctAnswer: ak || "",
            };
          })
        );

        const validItems = items.filter((it) => it && it.id !== null);
        validItems.sort((a, b) => a.id - b.id);

        if (!cancelled) {
          setQuestions(validItems);
          setTestMetadata({
            courseName: courseId.toUpperCase(),
            testName: testId.replace(/_/g, " "),
            category,
          });
        }
      } catch (err) {
        console.error("Error loading test questions:", err);
        if (!cancelled) {
          setError("Failed to load test questions. Please try again.");
          setQuestions([]);
        }
      } finally {
        if (!cancelled) setLoadingQuestions(false);
      }
    };

    fetchQuestions();
    return () => {
      cancelled = true;
    };
  }, [courseId, testId]);

  // Timer Effect
  useEffect(() => {
    if (showTest && !showResult) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [showTest, showResult]);

  // Utility Functions
  const formatTime = (sec) => {
    const h = String(Math.floor(sec / 3600)).padStart(2, "0");
    const m = String(Math.floor((sec % 3600) / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  const handleStartTestClick = () => {
    if (currentUser) {
      setShowInstructions(true);
    } else {
      setShowLoginPopup(true);
    }
  };

  const handleLoginClick = () => {
    setShowLoginPopup(false);
    // Note: Implement Google login if needed
  };

  const handleProceedToTest = () => {
    if (agreeChecked) {
      setShowInstructions(false);
      setShowTest(true);
    }
  };

  const handleAnswerChange = (value) => {
    if (!questions || !questions[currentQues]) return;
    setAnswers({ ...answers, [questions[currentQues].id]: value });
  };

  const handleSaveNext = () => {
    if (currentQues < questions.length - 1) setCurrentQues(currentQues + 1);
  };

  const handleClear = () => {
    if (!questions || !questions[currentQues]) return;
    setAnswers({ ...answers, [questions[currentQues].id]: "" });
  };

  const handleMarkReview = () => {
    if (!questions || !questions[currentQues]) return;
    if (!marked.includes(questions[currentQues].id))
      setMarked([...marked, questions[currentQues].id]);
    handleSaveNext();
  };

  const handleBack = () => {
    if (currentQues > 0) setCurrentQues(currentQues - 1);
  };

  const handleNext = () => {
    if (currentQues < questions.length - 1) setCurrentQues(currentQues + 1);
  };

  // Submit Test and Save Results
  const handleSubmit = async () => {
    setShowResult(true);
    setShowTest(false);

    const totalQuestions = questions.length;
    let attempted = 0,
      correct = 0,
      incorrect = 0,
      totalMarks = 0,
      negativeMarks = 0;

    const questionDetails = questions.map((q) => {
      const userAnswer = answers[q.id] || null;
      const isCorrect =
        userAnswer !== null &&
        String(userAnswer).trim() === String(q.correctAnswer).trim();

      if (userAnswer !== null) {
        attempted++;
        if (isCorrect) {
          correct++;
          totalMarks += 4;
        } else {
          incorrect++;
          negativeMarks += 1;
        }
      }

      return {
        questionId: q.id,
        userAnswer: userAnswer,
        correctAnswer: q.correctAnswer,
        status:
          userAnswer === null ? "unattempted" : isCorrect ? "correct" : "incorrect",
      };
    });

    const finalMarks = totalMarks - negativeMarks;
    let newAttempt = 1;

    if (currentUser) {
      const userRef = doc(db, "users", currentUser.uid);
      try {
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          let testsTaken = userData.testsTaken || [];
          const testName = `${testMetadata?.category} - ${testMetadata?.testName}`;

          let existingTestIndex = testsTaken.findIndex(
            (t) => t.testName === testName
          );
          if (existingTestIndex !== -1) {
            newAttempt = (testsTaken[existingTestIndex].attempt || 0) + 1;
            testsTaken.splice(existingTestIndex, 1);
          }

          const resultData = {
            testName: testName,
            score: finalMarks,
            total: totalQuestions * 4,
            attempted: attempted,
            correct: correct,
            incorrect: incorrect,
            totalQuestions: totalQuestions,
            date: new Date().toISOString(),
            attempt: newAttempt,
            storagePath: `PremiumTestSeries/${testMetadata?.category}/${testId}`,
            questionDetails: questionDetails,
          };

          testsTaken.push(resultData);
          await updateDoc(userRef, { testsTaken: testsTaken });
          console.log("Test result updated successfully!");
        }
      } catch (err) {
        console.error("Error updating result:", err);
      }
    }
  };

  // Loading State
  if (loadingQuestions) {
    return (
      <div className="mock-container">
        <div className="mock-header">
          <h2>Loading test...</h2>
        </div>
        <div className="mock-body">
          <div className="question-area" style={{ textAlign: "center" }}>
            <p>Fetching questions from server. Please wait.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mock-container">
        <div className="mock-header">
          <h2>Error</h2>
        </div>
        <div className="mock-body">
          <div className="question-area" style={{ textAlign: "center" }}>
            <p style={{ color: "#ef4444" }}>{error}</p>
            <button
              className="back-btn"
              onClick={() => navigate(`/purchased-tests/${courseId}`)}
              style={{ marginTop: "20px" }}
            >
              ← Back to Tests
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Initial Page (Before Instructions)
  if (!showInstructions && !showTest && !showResult) {
    return (
      <div className="initial-page-container">
        <div className="test-info-box">
          <h2>{testMetadata?.testName}</h2>
          <p>
            <strong>Category:</strong> {testMetadata?.category}
          </p>
          <p>
            <strong>Duration:</strong> 3 Hours
          </p>
          <p>
            <strong>Total Questions:</strong> {questions.length}
          </p>
          <p>
            <strong>Type:</strong> Multiple Choice & Numerical
          </p>
          <p>
            <strong>Marking:</strong> +4 for correct, -1 for incorrect, 0 for
            unattempted
          </p>
          <div className="button-row">
            <button className="start-test-btn" onClick={handleStartTestClick}>
              Start Test
            </button>
            <button
              className="back-btn"
              onClick={() => navigate(`/purchased-tests/${courseId}`)}
            >
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Instructions Page
  if (showInstructions) {
    return (
      <div className="instructions-container">
        <h2>Please read the instructions carefully</h2>
        <div className="instruction-box">
          <h3>General Instructions:</h3>
          <p>Total duration of this test is 180 minutes.</p>
          <p>
            The clock will be set at the server. The countdown timer in the top
            right corner will display the remaining time. When the timer reaches
            zero, the exam will end automatically.
          </p>
          <h4>Question Palette:</h4>
          <ul>
            <li>🟢 You have answered the question.</li>
            <li>🟡 You have marked this question for review.</li>
            <li>⚪ Question not answered yet.</li>
          </ul>
          <h4>Navigation:</h4>
          <ul>
            <li>Click "Save & Next" to save and move to the next question.</li>
            <li>Click "Mark for Review & Next" to mark and move forward.</li>
            <li>Use Back/Next buttons to navigate between questions.</li>
          </ul>
          <p className="declaration">
            I have read and understood the instructions. I declare that I am not
            in possession of any prohibited material. I agree to abide by all
            exam rules.
          </p>
        </div>
        <div className="agree-section">
          <label>
            <input
              type="checkbox"
              checked={agreeChecked}
              onChange={(e) => setAgreeChecked(e.target.checked)}
            />
            I agree to the above instructions
          </label>
          <button
            className="proceed-btn"
            disabled={!agreeChecked}
            onClick={handleProceedToTest}
          >
            Start Test ➡
          </button>
        </div>
      </div>
    );
  }

  // Test Page
  if (showTest) {
    if (!questions || questions.length === 0) {
      return (
        <div className="mock-container">
          <div className="mock-header">
            <h3>No questions found</h3>
          </div>
          <div className="mock-body">
            <div className="question-area">
              <p>Unable to load test questions.</p>
              <button
                className="back-btn"
                onClick={() => navigate(`/purchased-tests/${courseId}`)}
              >
                ← Back
              </button>
            </div>
          </div>
        </div>
      );
    }

    const q = questions[currentQues];

    return (
      <div className="mock-container">
        <div className="mock-header">
          <div className="user-info">
            <div className="user-icon">👤</div>
            <div>
              <p>
                <strong>Candidate:</strong> {userName || "Guest"}
              </p>
              <p>
                <strong>Test:</strong> {testMetadata?.testName}
              </p>
              <p>
                <strong>Question:</strong> {currentQues + 1} of {questions.length}
              </p>
            </div>
          </div>
          <div className="timer">
            Remaining Time: <span className="timer-value">{formatTime(timeLeft)}</span>
          </div>
        </div>

        <div className="mock-body">
          <div className="question-area">
            {q.img && (
              <div style={{ margin: "8px 0" }}>
                <img
                  src={q.img}
                  alt={`Q${q.id}`}
                  style={{ maxWidth: "100%", height: "auto" }}
                />
              </div>
            )}
            {q.type === "mcq" && (
              <div className="options">
                {[1, 2, 3, 4].map((n) => {
                  const val = String(n);
                  return (
                    <label key={n} className="option">
                      <input
                        type="radio"
                        name={`q${q.id}`}
                        value={val}
                        checked={answers[q.id] === val}
                        onChange={(e) => handleAnswerChange(e.target.value)}
                      />
                      {`${val})`}
                    </label>
                  );
                })}
              </div>
            )}
            {q.type === "numerical" && (
              <input
                type="text"
                className="numeric-input"
                value={answers[q.id] || ""}
                onChange={(e) => handleAnswerChange(e.target.value)}
                placeholder="Enter your answer"
              />
            )}
            <div className="button-row">
              <button className="save-btn" onClick={handleSaveNext}>
                Save & Next
              </button>
              <button className="clear-btn" onClick={handleClear}>
                Clear Response
              </button>
              <button className="mark-btn" onClick={handleMarkReview}>
                Mark for Review & Next
              </button>
            </div>
            <div className="button-row" style={{ marginTop: "15px" }}>
              <button className="nav-btn" onClick={handleBack}>
                Back
              </button>
              <button className="nav-btn" onClick={handleNext}>
                Next
              </button>
              <button className="submit-btn" onClick={handleSubmit}>
                Submit Test
              </button>
              <button
                className="close-btn1"
                onClick={() => navigate(`/purchased-tests/${courseId}`)}
              >
                ❌ Close Test
              </button>
            </div>
          </div>
          <div className="question-palette">
            <h4>Question Palette</h4>
            <div className="palette-grid">
              {questions.map((qq, i) => (
                <button
                  key={qq.id}
                  className={`palette-btn ${
                    answers[qq.id] ? "answered" : ""
                  } ${marked.includes(qq.id) ? "marked" : ""} ${
                    currentQues === i ? "active" : ""
                  }`}
                  onClick={() => setCurrentQues(i)}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Results Page
  if (showResult) {
    const totalQuestions = questions.length;
    let attempted = 0,
      correct = 0,
      incorrect = 0;

    questions.forEach((q) => {
      const ans = answers[q.id];
      if (ans) {
        attempted++;
        if (String(ans).trim() === String(q.correctAnswer).trim())
          correct++;
        else incorrect++;
      }
    });

    const finalMarks = correct * 4 - incorrect * 1;

    const data = {
      labels: ["Correct", "Incorrect", "Unattempted"],
      datasets: [
        {
          label: "Questions Analysis",
          data: [correct, incorrect, totalQuestions - attempted],
          backgroundColor: ["#10b981", "#ef4444", "#6b7280"],
        },
      ],
    };

    return (
      <div className="mock-container">
        <div className="mock-header">
          <h2>Test Result & Analysis</h2>
          <div className="button-row">
            <button
              className="close-btn2"
              onClick={() => navigate(`/purchased-tests/${courseId}`)}
            >
              ⬅️ Back to Tests
            </button>
          </div>
        </div>
        <div className="mock-body" id="result-summary-section">
          <div className="question-area" style={{ flex: 1 }}>
            <div className="result-summary">
              <h3>Candidate: {userName}</h3>
              <div className="summary-cards">
                <div className="card attempted">Attempted: {attempted}</div>
                <div className="card correct">Correct: {correct}</div>
                <div className="card incorrect">Incorrect: {incorrect}</div>
                <div className="card total">Total: {totalQuestions}</div>
                <div className="card marks">
                  Score: {finalMarks} / {totalQuestions * 4}
                </div>
              </div>
            </div>

            <h4 className="detail-heading">Graphical Analysis:</h4>
            <Bar
              data={data}
              options={{
                responsive: true,
                plugins: { legend: { display: false } },
              }}
            />

            <h4 className="detail-heading">Question-wise Details:</h4>
            <div className="question-details">
              {questions.map((q, i) => (
                <div
                  key={q.id}
                  className={`question-detail ${
                    answers[q.id] === q.correctAnswer
                      ? "correct"
                      : answers[q.id]
                      ? "incorrect"
                      : "unattempted"
                  }`}
                >
                  {q.img && (
                    <div style={{ marginBottom: 8 }}>
                      <img
                        src={q.img}
                        alt={`Q${i + 1}`}
                        className="question-detail-img"
                      />
                    </div>
                  )}
                  <p>
                    Your Answer: {answers[q.id] || "Not Answered"} | Correct:{" "}
                    {q.correctAnswer || "—"}
                  </p>
                  <p>
                    Marks:{" "}
                    {answers[q.id] === q.correctAnswer
                      ? "+4"
                      : answers[q.id]
                      ? "-1"
                      : "0"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
